import { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import { api, formatApiError } from "../../lib/apiClient";

const EMPTY = { title: "", content: "", active: true };

export default function PopupsManager() {
    const [popups, setPopups] = useState([]);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);

    const fetchAll = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/popups");
            setPopups(data);
        } catch (_e) {
            toast.error("Erreur de chargement");
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const reset = () => {
        setEditing(null);
        setForm(EMPTY);
    };

    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form, image_url: "", link_url: "" };
            if (editing) {
                await api.put(`/admin/popups/${editing}`, payload);
                toast.success("Pop-up mise à jour");
            } else {
                await api.post("/admin/popups", payload);
                toast.success("Pop-up créée");
            }
            reset();
            fetchAll();
        } catch (e2) {
            toast.error(formatApiError(e2.response?.data?.detail) || "Erreur");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer cette pop-up ?")) return;
        try {
            await api.delete(`/admin/popups/${id}`);
            toast.success("Supprimée");
            fetchAll();
        } catch (_e) {
            toast.error("Erreur");
        }
    };

    return (
        <div data-testid="admin-popups-page">
            <Toaster richColors position="top-center" />
            <p className="section-tag">administration</p>
            <h1 className="font-serif-display text-4xl text-[#2A1408] tracking-tight">
                Pop-ups
            </h1>
            <p className="text-[#5C3D2E] mt-2">
                Une seule pop-up est active à la fois sur le site public.
            </p>

            <section className="brand-card p-6 mt-6">
                <form onSubmit={save} className="grid md:grid-cols-2 gap-4">
                    <label className="md:col-span-2 text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Titre
                        <input
                            required
                            value={form.title}
                            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                            data-testid="popup-title"
                        />
                    </label>
                    <label className="md:col-span-2 text-xs uppercase tracking-widest text-[#5C3D2E]">
                        Contenu
                        <textarea
                            rows={4}
                            required
                            value={form.content}
                            onChange={(e) => setForm((f) => ({ ...f, content: e.target.value }))}
                            className="mt-1 w-full bg-white border border-[#E4D1BB] rounded-md px-3 py-2 text-sm"
                            data-testid="popup-content"
                        />
                    </label>
                    <label className="flex items-center gap-2 text-sm text-[#5C3D2E]">
                        <input
                            type="checkbox"
                            checked={form.active}
                            onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                        />
                        Active
                    </label>
                    <div className="md:col-span-2 flex gap-3">
                        <button type="submit" className="brand-btn" data-testid="popup-submit">
                            {editing ? "Mettre à jour" : "Créer"}
                        </button>
                        {editing && (
                            <button type="button" onClick={reset} className="brand-btn-outline">
                                Annuler
                            </button>
                        )}
                    </div>
                </form>
            </section>

            <section className="mt-8 space-y-3">
                {popups.map((p) => (
                    <article
                        key={p.id}
                        className="brand-card p-5 flex justify-between items-start gap-3"
                        data-testid={`admin-popup-${p.id}`}
                    >
                        <div>
                            <p className="text-xs uppercase tracking-widest text-[#B07B66]">
                                {p.active ? "Active" : "Inactive"}
                            </p>
                            <p className="font-serif-display text-lg text-[#2A1408]">{p.title}</p>
                            <p className="text-sm text-[#5C3D2E] line-clamp-2">{p.content}</p>
                        </div>
                        <div className="flex flex-col gap-2 shrink-0">
                            <button
                                type="button"
                                onClick={() => {
                                    setEditing(p.id);
                                    setForm({
                                        title: p.title,
                                        content: p.content,
                                        active: !!p.active,
                                    });
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                }}
                                className="text-[#6B4226]"
                                aria-label="Modifier"
                            >
                                <Pencil size={16} />
                            </button>
                            <button
                                type="button"
                                onClick={() => remove(p.id)}
                                className="text-red-700"
                                aria-label="Supprimer"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </article>
                ))}
            </section>
        </div>
    );
}
